import React from 'react';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { Navigate } from 'react-router-dom';
import { TafCheckboxInput, TafButton } from '@taf-ui/react';
import { TafChangeEvent } from '@taf-ui/core';
import AppStorage from 'Storage/Storage';
import { isBrand, translate, translateWithLink2 } from 'utils/utils';
import DraftService from 'Requests/DraftService/DraftService';
import FinalizeService from 'Requests/FinalizeService/FinalizeService';
import Steps from 'enums/Steps';
import Labels from 'enums/Labels';
import { Loader } from 'Loading/Loading';
import HealthDeclarationStatus from 'enums/HealthDeclarationStatus';
import { FormValueRadio } from 'interfaces/Underwriter/FormValue';
import ProductType from '../enums/ProductType';
import RouteParams from '../interfaces/RouteParams';
import CompleteState from '../interfaces/Complete';
import Brand from '../enums/Brand';

import ReadSpeakerReader from '../ReadSpeakerReader/';
import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';

class Complete extends React.PureComponent<RouteParams, CompleteState> {
  private static valueIsChangeEvent = (
    value: CustomEvent<TafChangeEvent<string>> | FormValueRadio,
  ): value is CustomEvent<TafChangeEvent<string>> => typeof value !== 'string';

  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly finalizeQSService: FinalizeService;

  private pathOfInvalidPage: string | null;

  private readonly underWriterIsMagnum: boolean;

  constructor(props: RouteParams) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.finalizeQSService = new FinalizeService();
    this.pathOfInvalidPage = null;
    this.state = {
      isFinalizing: false,
      navigateComponent: undefined,
    };

    this.underWriterIsMagnum =
      this.storage.getUnderwriterOwner === 'IPT_MAGNUM';
    if (this.underWriterIsMagnum) {
      this.updateRemarksState('NO');
    }
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.Complete, 'active', true);
    this.checkIfWeNeedToStoreDraft();
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    this.storage.modifyProgressBar(Steps.Complete, 'active', false);
  }

  checkIfWeNeedToStoreDraft = (): void => {
    const currentHDStatus = this.storage.getCurrentHDStatus();
    const { isFinalizing } = this.state;
    if (
      currentHDStatus === HealthDeclarationStatus.IN_PROGRESS &&
      !isFinalizing
    ) {
      this.draftService.syncLater(500);
    }
  };

  handleIsConsent = (event: CustomEvent<TafChangeEvent<boolean>>): void => {
    this.storage.setIsConsent(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleAgreeWithPrivacyStatement = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsAgreeWithPrivacyStatement(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  checkIfPageValid = (): void => {
    this.storage.modifyProgressBar(
      Steps.Complete,
      'valid',
      this.getIsPageValid(),
    );
  };

  getIsPageValid = (): boolean => {
    return (
      this.storage.getIsAgreeWithPrivacyStatement && this.storage.getIsConsent
    );
  };

  checkPreviousStepsValidity = (): boolean =>
    this.storage
      .getProgressBarByUnderwriterOwner()
      .filter((step) => step.step !== Steps.Complete)
      .every((step) => {
        if (!step.valid) {
          this.setPathOfInvalidPage(step.path);
        }
        return step.valid;
      });

  setPathOfInvalidPage = (path: string | null): void => {
    this.pathOfInvalidPage = path;
  };

  proceedToSigning = (): void => {
    this.setPathOfInvalidPage(null);
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    const isWholeAppValid =
      this.checkPreviousStepsValidity() && this.getIsPageValid();
    if (isWholeAppValid) {
      this.finalizeQS();
    } else {
      this.redirectToInvalidPage();
    }
  };

  redirectToInvalidPage = (): void => {
    if (this.pathOfInvalidPage) {
      const { healthDeclarationId } = this.props;
      this.setState({
        navigateComponent: (
          <Navigate
            to={`/${healthDeclarationId}/${this.pathOfInvalidPage}`}
            replace={true}
          />
        ),
      });
    }
  };

  setIsFinalizing = (isFinalizing: boolean): void => {
    this.setState({
      isFinalizing,
    });
  };

  private updateRemarksState = (
    data: CustomEvent<TafChangeEvent<string>> | FormValueRadio,
  ): void => {
    let hasRemarks: FormValueRadio;

    if (Complete.valueIsChangeEvent(data)) {
      hasRemarks = data.detail.value as FormValueRadio;
    } else {
      hasRemarks = data;
    }

    this.storage.setHasRemarks(hasRemarks);
    if (hasRemarks === 'NO') {
      this.storage.setFinalComment('');
    }
  };

  finalizeQS(): void {
    this.setIsFinalizing(true);
    this.storage.setIsProgressBarHidden(true);
    this.finalizeQSService
      .finalize()
      .then((response) => {
        this.setIsFinalizing(false);
        this.storage.setIsProgressBarHidden(false);
        window.location.href = response.data.result.signUrl || '';
      })
      .catch(() => {
        const { healthDeclarationId } = this.props;
        this.setState({
          navigateComponent: (
            <Navigate to={`/${healthDeclarationId}/error`} replace={true} />
          ),
        });
      });
  }

  render(): React.ReactElement {
    const {
      phonePrefix = '',
      phoneNumberBody = '',
      surname = '',
      initials = '',
      firstName = '',
    } = this.storage.getPersonalDataFormState;
    const productType = this.storage.getProductType();

    let { middleName } = this.storage.getPersonalDataFormState;

    const productSpecificData = this.storage.getProductSpecificInfo;

    if (!middleName) {
      middleName = '';
    }

    const { isFinalizing, navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    const lang = this.storage.getLocale;

    const phone = `${phonePrefix}${phoneNumberBody}`;

    const fullName = isBrand(Brand.wefox)
      ? `${firstName} ${middleName} ${surname}`
      : `${initials} ${middleName} ${surname}`;

    const nameLabel =
      `${translate(Labels.COMPLETE_P3, lang)}, ` + `${fullName}, `;

    const nameLabelWithAgreed =
      nameLabel + `${translate(Labels.COMPLETE_P4, lang)}`;

    const labelWithPrivacyStatement = translateWithLink2(
      Labels.COMPLETE_CHECKBOX_LIST5,
      lang,
      [productSpecificData.privacyStatementLink],
    ).dangerouslySetInnerHTML.__html;

    const labelWithConsent =
      nameLabel +
      translateWithLink2(Labels.COMPLETE_CHECKBOX_LIST7, lang, [
        productSpecificData.applicationFormDownloadLink,
        productSpecificData.insurersLink,
      ]).dangerouslySetInnerHTML.__html;

    if (!this.storage.getToken || isFinalizing) {
      return <Loader />;
    }

    const getListElementWithUrl = (element: string) => (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <li
        data-testid="COMPLETE_CHECKBOX_LIST5"
        dangerouslySetInnerHTML={{ __html: element }}
      ></li>
    );

    return (
      <div
        className="completeScreen completeScreen-taf"
        id="main-content"
        lang={languageToReadSpeakerLang(lang)}
      >
        <ReadSpeakerReader />
        <h1>{translate(Labels.COMPLETE_HEADER, lang)}</h1>

        <p>{translate(Labels.COMPLETE_P1, lang)}</p>
        <p className="bold">
          <span>
            {translate(Labels.COMPLETE_P3_TAF, lang)}
            {phone}
          </span>
        </p>
        <p>{translate(Labels.COMPLETE_P4_1_TAF, lang)}</p>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsAgreeWithPrivacyStatement &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsAgreeWithPrivacyStatement}
            name="isAgreeWithPrivacyStatement"
            inputId="isAgreeWithPrivacyStatement"
            label={nameLabelWithAgreed}
            onTafChange={this.handleAgreeWithPrivacyStatement}
            data-testid="isAgreeWithPrivacyStatement"
          />
        </div>
        <ul>
          <li data-testid="COMPLETE_CHECKBOX_LIST1">
            {translate(Labels.COMPLETE_CHECKBOX_LIST1, lang)}
          </li>
          <li data-testid="COMPLETE_CHECKBOX_LIST2">
            {translate(Labels.COMPLETE_CHECKBOX_LIST2, lang)}
          </li>
          <li data-testid="COMPLETE_CHECKBOX_LIST3">
            {translate(Labels.COMPLETE_CHECKBOX_LIST3, lang)}
          </li>
          <li data-testid="COMPLETE_CHECKBOX_LIST4">
            {translate(Labels.COMPLETE_CHECKBOX_LIST4, lang)}
          </li>
          {getListElementWithUrl(labelWithPrivacyStatement)}
          <li data-testid="COMPLETE_CHECKBOX_LIST6">
            {translate(Labels.COMPLETE_CHECKBOX_LIST6, lang)}
          </li>
        </ul>
        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsConsent && this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsConsent}
            name="isConsent"
            inputId="isConsent"
            data-testid="isConsent"
            label={labelWithConsent}
            onTafChange={this.handleIsConsent}
          />
        </div>
        <div className="signingBtn">
          <TafButton
            id="goToSigning"
            onClick={this.proceedToSigning}
            type="button"
            primary={true}
            disabled={isFinalizing}
            title={translate(Labels.PROCEED_TO_SIGNING, lang)}
          >
            {translate(Labels.PROCEED_TO_SIGNING, lang)}
          </TafButton>
        </div>
      </div>
    );
  }
}

export default observer(Complete);
